/**
 * Shared types and utilities for geometry worker modules.
 */
import * as THREE from 'three'
import {Brush, Evaluator, SUBTRACTION} from 'three-bvh-csg'
import {mergeGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import {createRoundedRectShape} from '../../utils/geometry'
import {geometryQuality} from './quality'

// ── Hardcoded geometry constants (not user-configurable) ─────────
export const FRONT_FACE_EXTRUDE = 0.5
export const FRONT_FACE_GAP = 0.1
export const DISPLAY_EXTRUDE = 0.3

// ── Shadow insert material (slot/cap planes at cutout mouths) ────
export const SHADOW_INSERT_COLOR = '#1a1a1a'
export const SHADOW_INSERT_OPACITY = 0.6

// ── Serialization ─────────────────────────────────────────────────

export interface SerializedGeo {
  position: Float32Array
  normal: Float32Array
  uv: Float32Array | null
  index: Uint32Array | null
}

export const serializeGeo = (geo: THREE.BufferGeometry): SerializedGeo => {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined
  if (!pos) {
    return {position: new Float32Array(0), normal: new Float32Array(0), uv: null, index: null}
  }
  const norm = geo.getAttribute('normal') as THREE.BufferAttribute | null
  const uv = geo.getAttribute('uv') as THREE.BufferAttribute | null
  const idx = geo.getIndex()

  return {
    position: new Float32Array(pos.array),
    normal: norm ? new Float32Array(norm.array) : new Float32Array(pos.count * 3),
    uv: uv ? new Float32Array(uv.array) : null,
    index: idx ? new Uint32Array(idx.array) : null,
  }
}

export const collectTransferables = (s: SerializedGeo): ArrayBuffer[] => {
  const bufs: ArrayBuffer[] = [s.position.buffer as ArrayBuffer, s.normal.buffer as ArrayBuffer]
  if (s.uv) bufs.push(s.uv.buffer as ArrayBuffer)
  if (s.index) bufs.push(s.index.buffer as ArrayBuffer)
  return bufs
}

// ── Batched cutter subtraction ────────────────────────────────────

/**
 * Reduce any cutter to a uniform, non-indexed `{position, normal}` geometry.
 *
 * The global body/front-face cut merges cutters from every feature type — plain
 * ExtrudeGeometry (non-indexed, has uv), BoxGeometry/CylinderGeometry (indexed),
 * and CSG-ADDITION output (non-indexed, no uv after the position/normal-only
 * evaluator). `mergeGeometries` refuses a set that mixes indexed/non-indexed or
 * differing attribute sets, so we normalise every cutter to the same shape first.
 * A subtraction cutter needs no UVs, and the result's normals are recomputed.
 */
const normalizeCutter = (geo: THREE.BufferGeometry): THREE.BufferGeometry => {
  const src = geo.getIndex() ? geo.toNonIndexed() : geo
  const out = new THREE.BufferGeometry()
  out.setAttribute('position', src.getAttribute('position'))
  const normal = src.getAttribute('normal')
  if (normal) out.setAttribute('normal', normal)
  return out
}

const subtractOne = (
  ev: Evaluator,
  mat: THREE.MeshStandardMaterial,
  target: THREE.BufferGeometry,
  cutter: THREE.BufferGeometry,
): THREE.BufferGeometry => {
  const a = new Brush(target, mat)
  a.updateMatrixWorld()
  const b = new Brush(cutter, mat)
  b.updateMatrixWorld()
  return ev.evaluate(a, b, SUBTRACTION).geometry
}

/**
 * Subtract every cutter from `target` in ONE CSG op — merge all cutters, then a
 * single SUBTRACTION so the target's BVH is rebuilt once instead of once per
 * feature type (the dominant CSG cost on feature-dense devices). Subtracting a
 * merged disjoint cutter is equivalent to subtracting each in turn. Falls back to
 * sequential subtraction only if the merge fails (rare after normalisation).
 * Recomputes normals; callers that need UVs (the body) re-derive them afterwards.
 */
export const subtractCutters = (
  ev: Evaluator,
  mat: THREE.MeshStandardMaterial,
  target: THREE.BufferGeometry,
  cutters: THREE.BufferGeometry[],
): THREE.BufferGeometry => {
  if (cutters.length === 0) return target
  const normalized = cutters.map(normalizeCutter)
  const merged = mergeGeometries(normalized)
  let result = target
  if (merged) {
    result = subtractOne(ev, mat, result, merged)
  } else {
    for (const cutter of normalized) result = subtractOne(ev, mat, result, cutter)
  }
  result.computeVertexNormals()
  return result
}

// ── Body params ───────────────────────────────────────────────────

export interface BodyParams {
  innerW: number
  innerH: number
  innerD: number
  clampedCorner: number
  clampedBevel: number
}

export const createBodyExtruded = (p: BodyParams) => {
  const shape = createRoundedRectShape(p.innerW, p.innerH, p.clampedCorner)
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: p.innerD,
    bevelEnabled: p.clampedBevel > 0,
    bevelThickness: p.clampedBevel,
    bevelSize: p.clampedBevel,
    // The body is the base mesh EVERY feature CSG subtracts from, so its poly
    // count drives the whole pipeline's CSG cost. Full detail for editor +
    // full-size renders; reduced only for thumbnails (see quality.ts).
    bevelSegments: geometryQuality.bodyBevelSegments,
    curveSegments: geometryQuality.bodyCurveSegments,
  })
  geo.translate(0, 0, -(p.innerD / 2))
  return geo
}
